import React, { useEffect, useState, useCallback } from 'react';
import { BarChart3, Bot, TrendingUp, Activity, MessageSquare, Target, Users, Shield } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { API_URL } from '../config/api';
import { useClient } from '../contexts/ClientContext';
import { useAgentAnalytics } from '../hooks/useApi';
import EvolutionGraphic from './GraficoEvolucionLlamadas';
import TabSystem from './TabSystem';
import { getTabsConfig } from '../config/lookerDashboards';
import FloatingParticles from './FloatingParticles';
import PageHeader from './ui/PageHeader';
import SubestadosHierarquicos from './SubestadosHierarquicos';
import '../styles/neutral-theme.css';

const DASHBOARD_CONFIG_FALLBACK = {
  sections: {
    analytics_ai: {
      enabled: true,
      dataSource: 'main',
      fields: {
        total_calls: true,
        total_calls_completed: true,
        total_calls_failed: true,
        avg_duration: true,
        chart_by_hour: true,
        response_rate: true,
        calls_by_hours_completed: true,
        agent_id: true,
        agent_name: true,
        start_time: true,
        status: true,
        call_duration_secs: true,
        // Campos opcionales para segunda fila de tarjetas KPI principales
        custom_kpi_1: false,
        custom_kpi_2: false,
        custom_kpi_3: false,
        custom_kpi_4: false,
        custom_kpi_5: false,
        // Campos específicos para la tabla "Resultados de Agentes"
        agent_table_total_calls: true,
        agent_table_completed: true,
        agent_table_failed: true,
        agent_table_avg_duration: true,
        agent_table_response_rate: true,
        // Campos opcionales adicionales para tabla de agentes
        agent_table_custom_1: false,
        agent_table_custom_2: false,
        agent_table_custom_3: false,
        agent_table_custom_4: false,
        agent_table_custom_5: false,
      },
      fieldMappings: {
        agent_id: { table: 'datos_agente_ia', column: 'agent_id' },
        agent_name: { table: 'datos_agente_ia', column: 'agent_name' },
        start_time: { table: 'datos_agente_ia', column: 'start_time' },
        status: { table: 'datos_agente_ia', column: 'status' },
        call_duration_secs: { table: 'datos_agente_ia', column: 'call_duration_secs' },
      },
      fieldCalculations: {},
    },
  },
};

const AgentAnalyticsPanel = () => {
  const { t } = useLanguage();
  const { clientConfig } = useClient();
  
  // Estado para fechas
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  
  // El panel de agente debe cargar datos sólo cuando se muestre
  const { data, loading, error, refetch } = useAgentAnalytics({ 
    enablePolling: false, 
    enabled: true,
    startDate: startDate,
    endDate: endDate
  });
  const [dashboardConfig, setDashboardConfig] = useState(DASHBOARD_CONFIG_FALLBACK);
  const [dashboardDefaults, setDashboardDefaults] = useState(DASHBOARD_CONFIG_FALLBACK);
  const [configLoading, setConfigLoading] = useState(false);
  
  // Estado para el modal de detalles
  const [detailsModal, setDetailsModal] = useState({
    isOpen: false,
    fieldKey: null,
    fieldLabel: null,
    loading: false,
    records: [],
    totalRecords: 0,
    error: null,
  });

  const [evolutionMetrics, setEvolutionMetrics] = useState({
    periodo: '-',
    totalLlamadas: 50,
    tasaRespuesta: 90,
    promedioRealizadas: 10,
    promedioContestadas: 9,
    horasActivas: 0,
  });

  useEffect(() => {
    const fetchConfig = async () => {
      setConfigLoading(true);
      try {
        const resp = await fetch(`${API_URL}/api/admin/dashboard-config`, { credentials: 'include' });
        if (resp.ok) {
          const json = await resp.json();
          setDashboardConfig(json.config || DASHBOARD_CONFIG_FALLBACK);
          setDashboardDefaults(json.defaults || DASHBOARD_CONFIG_FALLBACK);
        } else {
          setDashboardConfig(DASHBOARD_CONFIG_FALLBACK);
        }
      } catch (e) {
        setDashboardConfig(DASHBOARD_CONFIG_FALLBACK);
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const sectionCfg = (dashboardConfig?.sections?.analytics_ai)
    || (dashboardDefaults?.sections?.analytics_ai)
    || DASHBOARD_CONFIG_FALLBACK.sections.analytics_ai;
  const fieldsCfg = sectionCfg?.fields || {};
  const mappingsCfg = sectionCfg?.fieldMappings || {};
  const fieldFormats = sectionCfg?.fieldFormats || {};
  const fieldLabels = sectionCfg?.fieldLabels || {};
  const fieldCalculations = sectionCfg?.fieldCalculations || {};
  const fieldCalculationSpecs = sectionCfg?.fieldCalculationSpecs || {};
  const fieldAllowAudio = sectionCfg?.fieldAllowAudio || {}; // Configuración de campos que permiten audio
  const fieldOn = (key) => (fieldsCfg[key] ?? true);
  const isSectionEnabled = sectionCfg?.enabled !== false;
  
  // Función para verificar si un campo permite escuchar/descargar audio
  const allowsAudio = (fieldKey) => {
    if (!fieldKey) return false;
    // Si está explícitamente configurado, usar ese valor
    if (fieldAllowAudio.hasOwnProperty(fieldKey)) {
      return Boolean(fieldAllowAudio[fieldKey]);
    }
    // Por defecto, no permitir audio a menos que esté configurado explícitamente
    return false;
  };
  const mappingMissing = (key) => {
    const m = mappingsCfg[key];
    return !m || !m.table || !m.column;
  };
  
  // Función para verificar si un campo tiene COUNT en su cálculo Y es un número entero (no porcentaje)
  const hasCountCalculation = (fieldKey) => {
    if (!fieldKey) {
      return false;
    }
    
    // Verificar el formato del campo - SOLO números enteros pueden mostrar detalles
    const format = fieldFormats?.[fieldKey];
    // Solo permitir formato "integer" o sin formato (que se asume entero)
    // Rechazar explícitamente: percentage, decimal, currency, duration, date, datetime, time
    if (format && format !== 'integer') {
      console.log(`⚠️ Campo ${fieldKey} rechazado por formato: ${format}`);
      return false; // No mostrar detalles para ningún formato que no sea "integer"
    }
    
    const formula = fieldCalculations?.[fieldKey];
    const spec = fieldCalculationSpecs?.[fieldKey];
    
    // Verificar primero en el spec (tiene prioridad sobre fórmula)
    if (spec && Array.isArray(spec) && spec.length > 0) {
      // Buscar COUNT en el spec - verificar también COUNT_DISTINCT y COUNTD
      const hasCount = spec.some(item => {
        if (!item || typeof item !== 'object') {
          return false;
        }
        const func = item.func || item.agg;
        if (!func) {
          return false;
        }
        const funcUpper = String(func).toUpperCase();
        return funcUpper === 'COUNT' || funcUpper === 'COUNT_DISTINCT' || funcUpper === 'COUNTD';
      });
      
      if (hasCount) {
        console.log(`✅ Campo ${fieldKey} tiene COUNT en spec`);
        return true;
      }
    }
    
    // Si no hay spec o no tiene COUNT, verificar fórmula
    if (formula && typeof formula === 'string' && formula.trim()) {
      // Buscar COUNT, COUNT_DISTINCT o COUNTD en la fórmula
      const countPattern = /\b(COUNT|COUNT_DISTINCT|COUNTD)\s*\(/i;
      const hasCount = countPattern.test(formula);
      if (hasCount) {
        console.log(`✅ Campo ${fieldKey} tiene COUNT en fórmula`);
        return true;
      }
    }
    
    console.log(`⚠️ Campo ${fieldKey} no tiene COUNT en spec ni fórmula`);
    return false;
  };
  
  // Función para abrir el modal de detalles
  const openDetailsModal = async (fieldKey, fieldLabel) => {
    // Verificar que el campo tenga COUNT y formato integer
    if (!hasCountCalculation(fieldKey)) {
      console.log(`⚠️ Campo ${fieldKey} no tiene COUNT o formato válido, no se puede mostrar detalles`);
      return; // Solo abrir si tiene COUNT y formato válido
    }
    
    // Verificación adicional del formato (por si acaso)
    const format = fieldFormats[fieldKey];
    if (format && format !== 'integer') {
      console.log(`⚠️ Campo ${fieldKey} tiene formato ${format}, no se puede mostrar detalles`);
      alert(`El campo "${fieldLabel}" tiene formato ${format}. Solo se pueden ver detalles de campos con formato entero.`);
      return;
    }
    
    setDetailsModal({
      isOpen: true,
      fieldKey,
      fieldLabel,
      loading: true,
      records: [],
      totalRecords: 0,
      error: null,
    });
    
    try {
      const url = `${API_URL}/api/agent-analytics/details?field_key=${encodeURIComponent(fieldKey)}&start_date=${startDate}&end_date=${endDate}`;
      const resp = await fetch(url, { credentials: 'include' });
      
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({ detail: 'Error desconocido' }));
        throw new Error(errorData.detail || 'No se pudieron obtener los detalles');
      }
      
      const data = await resp.json();
      setDetailsModal(prev => ({
        ...prev,
        loading: false,
        records: data.records || [],
        totalRecords: data.total_records || 0,
        error: null,
      }));
    } catch (error) {
      setDetailsModal(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Error al cargar los detalles',
      }));
    }
  };
  
  // Función para cerrar el modal
  const closeDetailsModal = () => {
    setDetailsModal({
      isOpen: false,
      fieldKey: null,
      fieldLabel: null,
      loading: false,
      records: [],
      totalRecords: 0,
      error: null,
    });
  };

  // Estado para el reproductor de audio
  const [audioPlayer, setAudioPlayer] = useState({
    isOpen: false,
    audioUrl: null,
    conversationId: null,
    loading: false,
  });

  // Función auxiliar para obtener conversation_id desde hellobpo_llamadas
  const getConversationIdFromHellobpo = async (record) => {
    // Buscar cédula/documento/identificación en el registro - buscar en todas las columnas posibles
    let cedula = record.cedula || record.cedula_cliente || record.documento || record.id_cliente || 
                 record.identificacion || record.identificacion_cliente || record.numero_identificacion ||
                 record.numero_documento || record.documento_cliente || record.id;
    
    // Si no se encontró con los nombres comunes, buscar en todas las claves del registro
    if (!cedula) {
      const recordKeys = Object.keys(record);
      // Buscar columnas que contengan palabras clave relacionadas con identificación
      const cedulaKeywords = ['cedula', 'identificacion', 'documento', 'id_cliente', 'numero_identificacion'];
      
      for (const key of recordKeys) {
        const keyLower = key.toLowerCase();
        if (cedulaKeywords.some(keyword => keyLower.includes(keyword)) && record[key]) {
          // Verificar que el valor sea numérico o alfanumérico válido
          const value = String(record[key]).trim();
          if (value && value !== 'null' && value !== 'undefined' && value !== '-') {
            cedula = value;
            console.log(`📋 Cédula encontrada en columna: ${key} = ${cedula}`);
            break;
          }
        }
      }
    }
    
    // Buscar fecha en el registro - buscar en todas las columnas posibles
    let fecha = record.start_time || record.inicio_llamada || record.fecha_co || record.fecha || 
                record.created_at || record.fecha_llamada || record.fecha_inicio || record.timestamp ||
                record.fecha_creacion || record.date || record.fecha_hora;
    
    // Si no se encontró con los nombres comunes, buscar en todas las claves del registro
    if (!fecha) {
      const recordKeys = Object.keys(record);
      // Buscar columnas que contengan palabras clave relacionadas con fecha/hora
      const fechaKeywords = ['fecha', 'date', 'time', 'timestamp', 'inicio', 'created', 'start'];
      
      for (const key of recordKeys) {
        const keyLower = key.toLowerCase();
        if (fechaKeywords.some(keyword => keyLower.includes(keyword)) && record[key]) {
          const value = record[key];
          // Verificar que sea una fecha válida (string con formato de fecha o Date object)
          if (value && value !== 'null' && value !== 'undefined' && value !== '-') {
            fecha = value;
            console.log(`📅 Fecha encontrada en columna: ${key} = ${fecha}`);
            break;
          }
        }
      }
    }
    
    if (!cedula) {
      console.error('❌ Columnas disponibles en el registro:', Object.keys(record));
      throw new Error('No se encontró el número de cédula en el registro. Columnas disponibles: ' + Object.keys(record).join(', '));
    }
    
    if (!fecha) {
      console.error('❌ Columnas disponibles en el registro:', Object.keys(record));
      throw new Error('No se encontró la fecha de la llamada en el registro. Columnas disponibles: ' + Object.keys(record).join(', '));
    }
    
    // Formatear la fecha para la búsqueda
    let fechaFormateada = fecha;
    if (fecha instanceof Date) {
      fechaFormateada = fecha.toISOString().split('T')[0];
    } else if (typeof fecha === 'string') {
      // Si es una fecha ISO completa, tomar solo la parte de fecha
      if (fecha.includes('T')) {
        fechaFormateada = fecha.split('T')[0];
      } else if (fecha.includes(' ')) {
        fechaFormateada = fecha.split(' ')[0];
      }
    }
    
    try {
      // Buscar conversation_id en hellobpo_llamadas
      const response = await fetch(
        `${API_URL}/api/agent-analytics/conversation-id?cedula=${encodeURIComponent(cedula)}&fecha=${encodeURIComponent(fechaFormateada)}`
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Error desconocido' }));
        throw new Error(errorData.detail || `Error ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.conversation_id) {
        throw new Error(data.message || 'No se encontró el ID de conversación para esta llamada.');
      }
      
      return data.conversation_id;
    } catch (error) {
      console.error('Error al buscar conversation_id:', error);
      throw error;
    }
  };

  // Función para manejar ver/descargar transcripción
  const handleViewTranscript = async (record) => {
    try {
      // Primero intentar obtener conversation_id del registro directamente
      let conversationId = record.conversation_id || record.conversationId || 
                          record.conversation_id_elevenlabs || record.elevenlabs_conversation_id;
      
      // Si no está en el registro, buscarlo en hellobpo_llamadas
      if (!conversationId) {
        conversationId = await getConversationIdFromHellobpo(record);
      }
      
      if (!conversationId) {
        alert('No se encontró el ID de conversación para esta llamada.');
        return;
      }
      
      // Descargar el PDF de transcripción
      const response = await fetch(`${API_URL}/api/elevenlabs/pdf/${conversationId}`);
      
      if (!response.ok) {
        throw new Error('Error al obtener la transcripción');
      }
      
      // Crear blob y descargar
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transcripcion_${conversationId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error al descargar transcripción:', error);
      alert('Error al descargar la transcripción: ' + error.message);
    }
  };

  // Función para manejar descarga de audio
  const handleDownloadAudio = async (record) => {
    try {
      // Primero intentar obtener conversation_id del registro directamente
      let conversationId = record.conversation_id || record.conversationId || 
                          record.conversation_id_elevenlabs || record.elevenlabs_conversation_id;
      
      // Si no está en el registro, buscarlo en hellobpo_llamadas
      if (!conversationId) {
        conversationId = await getConversationIdFromHellobpo(record);
      }
      
      if (!conversationId) {
        alert('No se encontró el ID de conversación para esta llamada.');
        return;
      }
      
      // Descargar el audio
      const response = await fetch(`${API_URL}/api/elevenlabs/audio/${conversationId}`);
      
      if (!response.ok) {
        throw new Error('Error al obtener el audio');
      }
      
      // Crear blob y descargar
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversacion_${conversationId}.mp3`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error al descargar audio:', error);
      alert('Error al descargar el audio: ' + error.message);
    }
  };

  // Función para manejar reproducción de audio
  const handlePlayAudio = async (record) => {
    try {
      // Primero intentar obtener conversation_id del registro directamente
      let conversationId = record.conversation_id || record.conversationId || 
                          record.conversation_id_elevenlabs || record.elevenlabs_conversation_id;
      
      // Si no está en el registro, buscarlo en hellobpo_llamadas
      if (!conversationId) {
        conversationId = await getConversationIdFromHellobpo(record);
      }
      
      if (!conversationId) {
        alert('No se encontró el ID de conversación para esta llamada.');
        return;
      }
      
      setAudioPlayer({
        isOpen: true,
        audioUrl: `/api/elevenlabs/audio/${conversationId}`,
        conversationId: conversationId,
        loading: false,
      });
    } catch (error) {
      console.error('Error al reproducir audio:', error);
      alert('Error al reproducir el audio: ' + error.message);
    }
  };

  // Función para cerrar el reproductor de audio
  const closeAudioPlayer = () => {
    setAudioPlayer({
      isOpen: false,
      audioUrl: null,
      conversationId: null,
      loading: false,
    });
  };

  // Función para formatear valores según el formato configurado
  const formatFieldValue = (fieldKey, value) => {
    if (value === null || value === undefined) {
      return value;
    }
    const format = fieldFormats[fieldKey];
    if (!format) {
      // Sin formato específico, retornar tal cual
      return value;
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (isNaN(numValue)) {
      return value; // Si no es numérico, retornar tal cual
    }
    switch (format) {
      case 'integer':
        return Math.round(numValue).toLocaleString();
      case 'decimal':
        return numValue.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      case 'duration':
        // Convertir segundos a mm:ss
        const totalSeconds = Math.round(numValue);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
      case 'percentage':
        return `${numValue.toFixed(1)}%`;
      case 'currency':
        return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(numValue);
      case 'date':
        try {
          const date = new Date(value);
          return date.toLocaleDateString('es-ES');
        } catch {
          return value;
        }
      case 'datetime':
        try {
          const date = new Date(value);
          return date.toLocaleString('es-ES');
        } catch {
          return value;
        }
      case 'time':
        try {
          const date = new Date(value);
          return date.toLocaleTimeString('es-ES');
        } catch {
          return value;
        }
      default:
        return value;
    }
  };

  // Función para obtener el label personalizado o el por defecto
  const getFieldLabel = (fieldKey, defaultLabel) => {
    return fieldLabels[fieldKey] || defaultLabel;
  };

  // Adaptar tanto array (legacy) como objeto (nuevo backend) y aplicar config
  let metrics = [];
  let customMetrics = []; // Segunda fila de métricas opcionales
  if (isSectionEnabled && data && typeof data.metrics === 'object') {
    const m = data.metrics || {};
    if (fieldOn('total_calls')) {
      const rawValue = m.total_calls ?? 0;
      metrics.push({ 
        fieldKey: 'total_calls',
        label: getFieldLabel('total_calls', t('analytics.totalCalls')), 
        value: formatFieldValue('total_calls', rawValue)
      });
    }
    if (fieldOn('total_calls_completed')) {
      const rawValue = m.total_calls_completed ?? 0;
      metrics.push({ 
        fieldKey: 'total_calls_completed',
        label: getFieldLabel('total_calls_completed', 'Completadas'), 
        value: formatFieldValue('total_calls_completed', rawValue)
      });
    }
    if (fieldOn('total_calls_failed')) {
      const rawValue = m.total_calls_failed ?? 0;
      metrics.push({ 
        fieldKey: 'total_calls_failed',
        label: getFieldLabel('total_calls_failed', 'Fallidas'), 
        value: formatFieldValue('total_calls_failed', rawValue)
      });
    }
    if (fieldOn('avg_duration')) {
      const rawValue = m.avg_duration ?? 0;
      // Si viene como string formateado (ej: "0:00"), intentar parsearlo a segundos
      let numericValue = rawValue;
      if (typeof rawValue === 'string' && rawValue.includes(':')) {
        // Parsear formato mm:ss a segundos
        const parts = rawValue.split(':');
        if (parts.length === 2) {
          const minutes = parseInt(parts[0], 10) || 0;
          const seconds = parseInt(parts[1], 10) || 0;
          numericValue = minutes * 60 + seconds;
        } else {
          numericValue = parseFloat(rawValue) || 0;
        }
      }
      // Siempre formatear según el formato configurado
      const formattedValue = formatFieldValue('avg_duration', numericValue);
      metrics.push({ 
        fieldKey: 'avg_duration',
        label: getFieldLabel('avg_duration', t('analytics.avgDuration')), 
        value: formattedValue
      });
    }
    if (fieldOn('response_rate')) {
      const rawValue = m.response_rate ?? 0;
      // Si no hay formato configurado para response_rate, aplicar formato de porcentaje por defecto
      let formattedValue = formatFieldValue('response_rate', rawValue);
      if (!fieldFormats['response_rate']) {
        // Aplicar formato de porcentaje por defecto si no está configurado
        const numValue = typeof rawValue === 'string' ? parseFloat(rawValue) : Number(rawValue);
        if (!isNaN(numValue)) {
          formattedValue = `${numValue.toFixed(1)}%`;
        }
      }
      metrics.push({ 
        fieldKey: 'response_rate',
        label: getFieldLabel('response_rate', 'Tasa de Respuesta'), 
        value: formattedValue,
        isPercentage: true, // Marcar explícitamente como porcentaje para evitar que sea clicable
      });
    }
    
    // Campos opcionales KPI (segunda fila)
    // Mostrar si están habilitados, incluso si no tienen valor aún (mostrará 0)
    for (let i = 1; i <= 5; i++) {
      const fieldKey = `custom_kpi_${i}`;
      if (fieldOn(fieldKey)) {
        // El backend retorna el campo con valor 0 si está habilitado pero sin fórmula
        // Si no viene en la respuesta, también mostrar 0
        const rawValue = m[fieldKey] !== undefined && m[fieldKey] !== null ? m[fieldKey] : 0;
        const displayValue = formatFieldValue(fieldKey, rawValue);
        customMetrics.push({
          fieldKey: fieldKey,
          label: getFieldLabel(fieldKey, `Campo Personalizado ${i}`),
          value: displayValue
        });
      }
    }
  }

  // Campos principales (para métricas generales)
  const showTotalCalls = fieldOn('total_calls');
  const showCompleted = fieldOn('total_calls_completed');
  const showFailed = fieldOn('total_calls_failed');
  const showAvgDuration = fieldOn('avg_duration');
  const showResponseRate = fieldOn('response_rate');
  
  // Campos opcionales KPI (segunda fila)
  const showCustomKpi1 = fieldOn('custom_kpi_1');
  const showCustomKpi2 = fieldOn('custom_kpi_2');
  const showCustomKpi3 = fieldOn('custom_kpi_3');
  const showCustomKpi4 = fieldOn('custom_kpi_4');
  const showCustomKpi5 = fieldOn('custom_kpi_5');
  
  // Campos específicos para la tabla "Resultados de Agentes" (independientes)
  const showAgentTableTotalCalls = fieldOn('agent_table_total_calls');
  const showAgentTableCompleted = fieldOn('agent_table_completed');
  const showAgentTableFailed = fieldOn('agent_table_failed');
  const showAgentTableAvgDuration = fieldOn('agent_table_avg_duration');
  const showAgentTableResponseRate = fieldOn('agent_table_response_rate');
  
  // Campos opcionales para tabla de agentes
  const showAgentTableCustom1 = fieldOn('agent_table_custom_1');
  const showAgentTableCustom2 = fieldOn('agent_table_custom_2');
  const showAgentTableCustom3 = fieldOn('agent_table_custom_3');
  const showAgentTableCustom4 = fieldOn('agent_table_custom_4');
  const showAgentTableCustom5 = fieldOn('agent_table_custom_5');
  
  const showChart = fieldOn('chart_by_hour') || fieldOn('calls_by_hours_completed');

  const missingMappings = ['agent_id', 'start_time', 'status', 'call_duration_secs'].filter(mappingMissing);

  const hourlyCalls = showChart ? (data?.calls_by_hours || {}) : {};
  const hourlyAnswered = showChart ? (data?.calls_by_hours_answered || {}) : {};
  const answered = parseInt((metrics.find(m => m.label === t('analytics.attendedCalls'))?.value || '0').toString().replace(/,/g, ''), 10) || 0;
  const cancelled = parseInt((metrics.find(m => m.label === t('analytics.cancelledCalls'))?.value || '0').toString().replace(/,/g, ''), 10) || 0;
  const total = parseInt((metrics.find(m => m.label === t('analytics.totalCalls'))?.value || '0').toString().replace(/,/g, ''), 10) || 0;
  const notAnswered = Math.max(total - answered - cancelled, 0);

  const { locale } = useLanguage();

  const getLookerTabs = () => {
    const tabsConfig = getTabsConfig('analytics', locale, clientConfig);

    // Icon mapping
    const iconMapping = {
      'BarChart3': BarChart3,
      'MessageSquare': MessageSquare,
      'Activity': Activity,
      'Target': Target,
      'Users': Users,
      'Shield': Shield
    };

    return tabsConfig.map(tab => ({
        ...tab,
      icon: iconMapping[tab.icon] || BarChart3
    }));
  };

  const handleLookerTabChange = (tabId) => {
    // Tab changed
  };
  
  return (
    <div className="analytics-panel dashboard-container">
      <FloatingParticles count={25} />
    
      <PageHeader
        title={'Analíticas de Agente IA'}
        subtitle="Métricas directas realizadas por el agente IA"
        icon={<Bot />}
      />
      
      {/* Selector de fechas - Estilo integrado con diseño real */}
      <div className="card card-enhanced" style={{ 
        marginBottom: '24px',
        background: '#ffffff',
        border: '1px solid rgba(12, 44, 108, 0.15)',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(12, 44, 108, 0.08)',
        padding: '20px'
      }}>
        <div style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flex: '1',
            minWidth: '200px'
          }}>
            <label style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#0c2c6c',
              whiteSpace: 'nowrap'
            }}>
              Desde:
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (e.target.value > endDate) {
                  setEndDate(e.target.value);
                }
              }}
              max={today}
              style={{
                flex: '1',
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid rgba(12, 44, 108, 0.15)',
                background: '#ffffff',
                color: '#0c2c6c',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(12, 44, 108, 0.05)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#f46f42';
                e.target.style.boxShadow = '0 4px 12px rgba(244, 111, 66, 0.2)';
                e.target.style.outline = 'none';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(12, 44, 108, 0.15)';
                e.target.style.boxShadow = '0 2px 4px rgba(12, 44, 108, 0.05)';
              }}
            />
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flex: '1',
            minWidth: '200px'
          }}>
            <label style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#0c2c6c',
              whiteSpace: 'nowrap'
            }}>
              Hasta:
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                if (e.target.value < startDate) {
                  setStartDate(e.target.value);
                }
              }}
              min={startDate}
              max={today}
              style={{
                flex: '1',
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid rgba(12, 44, 108, 0.15)',
                background: '#ffffff',
                color: '#0c2c6c',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(12, 44, 108, 0.05)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#f46f42';
                e.target.style.boxShadow = '0 4px 12px rgba(244, 111, 66, 0.2)';
                e.target.style.outline = 'none';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(12, 44, 108, 0.15)';
                e.target.style.boxShadow = '0 2px 4px rgba(12, 44, 108, 0.05)';
              }}
            />
          </div>
          
          <button
            onClick={() => {
              setStartDate(today);
              setEndDate(today);
            }}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: '1px solid #f46f42',
              background: '#f46f42',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(244, 111, 66, 0.3)',
              whiteSpace: 'nowrap'
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#e55a2e';
              e.target.style.borderColor = '#e55a2e';
              e.target.style.boxShadow = '0 4px 12px rgba(244, 111, 66, 0.4)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = '#f46f42';
              e.target.style.borderColor = '#f46f42';
              e.target.style.boxShadow = '0 2px 8px rgba(244, 111, 66, 0.3)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            Hoy
          </button>
          
          {data?.date_range && (
            <div style={{
              marginLeft: 'auto',
              padding: '8px 14px',
              background: 'rgba(12, 44, 108, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(12, 44, 108, 0.1)'
            }}>
              <span style={{
                fontSize: '13px',
                color: '#0c2c6c',
                fontWeight: '500'
              }}>
                Mostrando: <strong>{data.date_range.start_date}</strong> a <strong>{data.date_range.end_date}</strong>
              </span>
            </div>
          )}
        </div>
      </div>
      
      {loading && (
        <div className="loading-enhanced">
          <div className="spinner-enhanced"></div>
          <div className="loading-text">Cargando analíticas del agente...</div>
        </div>
      )}

       {error && (
         <div className="error-message">{error}</div>
       )}

    {!configLoading && !isSectionEnabled && (
      <div className="message-enhanced info" style={{ marginTop: '12px' }}>
        Esta sección está desactivada en la configuración del cliente.
      </div>
    )}
    {!configLoading && isSectionEnabled && missingMappings.length > 0 && (
      <div className="message-enhanced warning" style={{ marginTop: '12px' }}>
        Faltan mapeos para: {missingMappings.join(', ')}. Completa tabla/columna en Configurar vistas.
      </div>
       )}
    {/* ============================================
       Panel de Métricas Generales
       ============================================ */}

    {/* Main Metrics Ultra Modern - Primera fila */}
    {!loading && !error && isSectionEnabled && metrics.length > 0 && (
      <div className="metrics-grid">
        {metrics.map((metric, index) => {
          const fieldKey = metric.fieldKey;
          // Verificar si es porcentaje explícitamente o tiene formato percentage
          const isPercentage = metric.isPercentage || (fieldKey && fieldFormats[fieldKey] === 'percentage');
          const isClickable = fieldKey && !isPercentage && hasCountCalculation(fieldKey);
          
          return (
            <div 
              key={index} 
              className="metric-card-ultra-modern"
              onClick={() => isClickable && openDetailsModal(fieldKey, metric.label)}
              style={{
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                if (isClickable) {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(12, 44, 108, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (isClickable) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }
              }}
            >
              <div className="metric-header-ultra">
                <div className="metric-label-container-ultra">
                  <span className="metric-label-ultra">{metric.label}</span>
                  {isClickable && (
                    <span style={{
                      fontSize: '11px',
                      color: '#f46f42',
                      marginLeft: '8px',
                      fontWeight: '500',
                    }}>
                      (Click para ver detalles)
                    </span>
                  )}
                </div>
                <div className="metric-icon-ultra">
                  <TrendingUp size={16} />
                </div>
              </div>
              
              <div className="metric-value-container-ultra">
                <div className="metric-value-ultra">{metric.value}</div>
                <div className="metric-value-glow"></div>
              </div>
              
              <div className="metric-background-ultra"></div>     
            </div>
          );
        })}
      </div>
    )}
    
    {/* Custom Metrics Ultra Modern - Segunda fila opcional */}
    {!loading && !error && isSectionEnabled && customMetrics.length > 0 && (
      <div className="metrics-grid" style={{ marginTop: '16px' }}>
        {customMetrics.map((metric, index) => {
          const fieldKey = metric.fieldKey;
          const isClickable = hasCountCalculation(fieldKey);
          
          return (
            <div 
              key={`custom-${index}`} 
              className="metric-card-ultra-modern"
              onClick={() => isClickable && openDetailsModal(fieldKey, metric.label)}
              style={{
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                if (isClickable) {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(12, 44, 108, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (isClickable) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }
              }}
            >
              <div className="metric-header-ultra">
                <div className="metric-label-container-ultra">
                  <span className="metric-label-ultra">{metric.label}</span>
                  {isClickable && (
                    <span style={{
                      fontSize: '11px',
                      color: '#f46f42',
                      marginLeft: '8px',
                      fontWeight: '500',
                    }}>
                      (Click para ver detalles)
                    </span>
                  )}
                </div>
                <div className="metric-icon-ultra">
                  <TrendingUp size={16} />
                </div>
              </div>
              
              <div className="metric-value-container-ultra">
                <div className="metric-value-ultra">{metric.value}</div>
                <div className="metric-value-glow"></div>
              </div>
              
              <div className="metric-background-ultra"></div>     
            </div>
          );
        })}
      </div>
    )}

    {isSectionEnabled && (
    <div className="charts-grid">
      {/* ============================================
       Panel de Subestados Jerárquicos
       ============================================ */}
      <div className="card card-enhanced animate-delay-200 call-evolution-ultra">
        <div className="card-header card-header-ultra">
          <div className="header-content">
            <div className="header-icon">
              <TrendingUp size={24} />
            </div>
            <div className="header-text">
              <h3 className="card-title">Desglose de Subestados</h3>
              <p className="card-subtitle">Análisis jerárquico de estados y subestados de llamadas</p>
            </div>
          </div>
          <div className="header-glow"></div>
        </div>
        <div className="card-content">
          <SubestadosHierarquicos 
            startDate={startDate}
            endDate={endDate}
            distributionProportionalConfig={sectionCfg?.subestadosConfig?.distributionProportionalKeys || []}
            analyticsData={data}
            fieldLabels={fieldLabels}
            fieldFormats={fieldFormats}
          />
        </div>
      </div>
    </div>
    )}

    {isSectionEnabled && showChart && (
    <div className="charts-grid">
      {/* ============================================
       Panel de Evolución de Llamadas
       ============================================ */}
      {/* Evolution Chart Ultra Modern */}
      <div className="card card-enhanced animate-delay-300 call-evolution-ultra">
        <div className="card-header card-header-ultra">
          <div className="header-content">
            <div className="header-icon">
              <Activity size={24} />
            </div>
            <div className="header-text">
          <h3 className="card-title">{t('analytics.callEvolution')}</h3>
              <p className="card-subtitle">Tendencia temporal de llamadas por período</p>
            </div>
          </div>
          <div className="header-glow"></div>
        </div>
        <div className="card-content">
          <div className="evolution-container-ultra">
            <div className="evolution-header-ultra">
              <div className="evolution-info">
                <div className="info-item">
                  <span className="info-label">Período</span>
                  <span className="info-value">{evolutionMetrics.periodo}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Total Llamadas</span>
                  <span className="info-value">{Number(evolutionMetrics.totalLlamadas || 0).toLocaleString()}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Promedio por Hora</span>
                  <span className="info-value">{`${evolutionMetrics.promedioRealizadas} / ${evolutionMetrics.promedioContestadas}`}</span>
                </div>
              </div>
            </div>
            <EvolutionGraphic 
              hourlyCalls={data?.evolution_metrics?.calls_by_hours || {}} 
              hourlyAnswered={data?.evolution_metrics?.calls_by_hours_completed || {}} 
              totalCompleted={data?.metrics?.total_calls_completed || 0}
              onMetricsChange={setEvolutionMetrics}
            />                         
          </div>
        </div>
      </div>
    </div>
    )}

    {isSectionEnabled && (
      <>
    {/* ============================================
       Panel de Resultados de Campañas
       ============================================ */}
    {/* Campaign Results Table */}
    <div className="card card-enhanced animate-delay-400 campaign-results-ultra">
      <div className="card-header card-header-ultra">
        <div className="header-content">
          <div className="header-icon">
            <BarChart3 size={24} />
          </div>
          <div className="header-text">
        <h3 className="card-title">Resultados de Agentes</h3>
            <p className="card-subtitle">Análisis detallado de rendimiento por Agente</p>
          </div>
        </div>
        <div className="header-glow"></div>
      </div>
      <div className="card-content">
        <div className="campaign-results-container">
          <div className="results-table-modern">
            <table className="results-table">
              <thead className="table-header-modern">
                <tr className="header-row">
                  <th className="header-cell campaign-col">Agentes</th>
                      {showAgentTableTotalCalls && <th className="header-cell metric-col">{getFieldLabel('agent_table_total_calls', 'Total Llamadas')}</th>}
                      {showAgentTableCompleted && <th className="header-cell metric-col">{getFieldLabel('agent_table_completed', 'Llamadas Completadas')}</th>}
                      {showAgentTableFailed && <th className="header-cell metric-col">{getFieldLabel('agent_table_failed', 'Llamadas Fallidas')}</th>}
                      {showAgentTableAvgDuration && <th className="header-cell metric-col">{getFieldLabel('agent_table_avg_duration', 'Duración Media')}</th>}
                      {showAgentTableResponseRate && <th className="header-cell metric-col">{getFieldLabel('agent_table_response_rate', 'Tasa de Respuesta')}</th>}
                      {showAgentTableCustom1 && <th className="header-cell metric-col">{getFieldLabel('agent_table_custom_1', 'Campo Personalizado 1')}</th>}
                      {showAgentTableCustom2 && <th className="header-cell metric-col">{getFieldLabel('agent_table_custom_2', 'Campo Personalizado 2')}</th>}
                      {showAgentTableCustom3 && <th className="header-cell metric-col">{getFieldLabel('agent_table_custom_3', 'Campo Personalizado 3')}</th>}
                      {showAgentTableCustom4 && <th className="header-cell metric-col">{getFieldLabel('agent_table_custom_4', 'Campo Personalizado 4')}</th>}
                      {showAgentTableCustom5 && <th className="header-cell metric-col">{getFieldLabel('agent_table_custom_5', 'Campo Personalizado 5')}</th>}
                </tr>
              </thead>
              <tbody className="table-body-modern">
                {(data?.agent_metrics?.agents || []).map((ag, index) => (
                  <tr key={ag.agent_id || index} className="campaign-row-modern">
                    <td className="row-cell campaign-cell">
                      <div className="campaign-info">
                        <div className="campaign-name">{ag.agent_name || ag.agent_id}</div>
                        <div className="campaign-indicator"></div>
                      </div>
                    </td>
                        {showAgentTableTotalCalls && (
                    <td className="row-cell metric-cell">
                      <div className="metric-container">
                        <span className="metric-value">{formatFieldValue('agent_table_total_calls', ag.agent_table_total_calls ?? ag.total_calls ?? 0)}</span>
                      </div>
                    </td>
                        )}
                        {showAgentTableCompleted && (
                    <td className="row-cell metric-cell">
                      <div className="metric-container">
                        <span className="metric-value">{formatFieldValue('agent_table_completed', ag.agent_table_done ?? ag.done ?? 0)}</span>
                      </div>
                    </td>
                        )}
                        {showAgentTableFailed && (
                    <td className="row-cell metric-cell">
                      <div className="metric-container">
                        <span className="metric-value">{formatFieldValue('agent_table_failed', ag.agent_table_failed ?? ag.failed ?? 0)}</span>
                      </div>
                    </td>
                        )}
                        {showAgentTableAvgDuration && (() => {
                      // Usar valores específicos de la tabla si existen; sino, fallback al base
                      let numericValue = ag.agent_table_avg_duration_secs ?? ag.agent_table_avg_duration ?? ag.avg_duration_secs ?? ag.avg_duration ?? 0;
                      if (typeof numericValue === 'string' && numericValue.includes(':')) {
                        // Parsear formato mm:ss a segundos
                        const parts = numericValue.split(':');
                        if (parts.length === 2) {
                          const minutes = parseInt(parts[0], 10) || 0;
                          const seconds = parseInt(parts[1], 10) || 0;
                          numericValue = minutes * 60 + seconds;
                        } else {
                          numericValue = parseFloat(numericValue) || 0;
                        }
                      }
                      const formattedValue = formatFieldValue('agent_table_avg_duration', numericValue);
                      return (
                    <td className="row-cell metric-cell">
                      <div className="metric-container">
                        <span className="metric-value">{formattedValue}</span>
                      </div>
                    </td>
                      );
                    })()}
                        {showAgentTableResponseRate && (
                    <td className="row-cell metric-cell">
                      <div className="metric-container">
                        <span className="metric-value">{formatFieldValue('agent_table_response_rate', ag.agent_table_response_rate ?? ag.response_rate ?? 0)}</span>
                      </div>
                    </td>
                        )}
                        {showAgentTableCustom1 && ag.agent_table_custom_1 !== undefined && ag.agent_table_custom_1 !== null && (
                    <td className="row-cell metric-cell">
                      <div className="metric-container">
                        <span className="metric-value">{formatFieldValue('agent_table_custom_1', ag.agent_table_custom_1)}</span>
                      </div>
                    </td>
                        )}
                        {showAgentTableCustom2 && ag.agent_table_custom_2 !== undefined && ag.agent_table_custom_2 !== null && (
                    <td className="row-cell metric-cell">
                      <div className="metric-container">
                        <span className="metric-value">{formatFieldValue('agent_table_custom_2', ag.agent_table_custom_2)}</span>
                      </div>
                    </td>
                        )}
                        {showAgentTableCustom3 && ag.agent_table_custom_3 !== undefined && ag.agent_table_custom_3 !== null && (
                    <td className="row-cell metric-cell">
                      <div className="metric-container">
                        <span className="metric-value">{formatFieldValue('agent_table_custom_3', ag.agent_table_custom_3)}</span>
                      </div>
                    </td>
                        )}
                        {showAgentTableCustom4 && ag.agent_table_custom_4 !== undefined && ag.agent_table_custom_4 !== null && (
                    <td className="row-cell metric-cell">
                      <div className="metric-container">
                        <span className="metric-value">{formatFieldValue('agent_table_custom_4', ag.agent_table_custom_4)}</span>
                      </div>
                    </td>
                        )}
                        {showAgentTableCustom5 && ag.agent_table_custom_5 !== undefined && ag.agent_table_custom_5 !== null && (
                    <td className="row-cell metric-cell">
                      <div className="metric-container">
                        <span className="metric-value">{formatFieldValue('agent_table_custom_5', ag.agent_table_custom_5)}</span>
                      </div>
                    </td>
                        )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
      </>
    )}

    {/* ============================================
       Panel de Informe Detallado - Análisis Avanzado
       ============================================ */}
    {/* Looker Dashboard Tabs */}
    {/* <div className="card">
      <div className="card-header card-header-ultra">
        <div className="header-content">
          <div className="header-icon">
            <BarChart3 size={24} />
          </div>
          <div className="header-text">
        <h3 className="card-title">{t('analytics.dashboardReport')}</h3>
        <p className="card-subtitle">{t('analytics.dashboardPlaceholder')}</p>
          </div>
        </div>
        <div className="header-glow"></div>
      </div>
      <div className="card-content">
        <TabSystem
          tabs={getLookerTabs()}
          defaultTab="general-metrics"
          style="modern"
          onTabChange={handleLookerTabChange}
          showIframeControls={true}
        />
      </div>
      </div>       */}
    
    {/* Modal de Detalles de Llamadas */}
    {detailsModal.isOpen && (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(12, 44, 108, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px',
        }}
        onClick={closeDetailsModal}
      >
        <div 
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(12, 44, 108, 0.3)',
            maxWidth: '95vw',
            maxHeight: '95vh',
            width: '1400px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header del Modal - Mejorado */}
          <div style={{
            padding: '28px 32px',
            borderBottom: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #0c2c6c 0%, #1a4a9c 100%)',
            boxShadow: '0 4px 12px rgba(12, 44, 108, 0.2)',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px',
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                }}>
                  📊
                </div>
                <h2 style={{
                  margin: 0,
                  fontSize: '28px',
                  fontWeight: '800',
                  color: '#ffffff',
                  letterSpacing: '-0.5px',
                }}>
                  {detailsModal.fieldLabel || 'Detalles de Llamadas'}
                </h2>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginLeft: '60px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 14px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#ffffff',
                }}>
                  <span>📋</span>
                  <span>{detailsModal.totalRecords} registro{detailsModal.totalRecords !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>
            <button
              onClick={closeDetailsModal}
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                width: '44px',
                height: '44px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                fontSize: '28px',
                fontWeight: '300',
                transition: 'all 0.3s ease',
                lineHeight: '1',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.target.style.transform = 'rotate(90deg) scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'rotate(0deg) scale(1)';
              }}
            >
              ×
            </button>
          </div>
          
          {/* Contenido del Modal - Mejorado */}
          <div style={{
            padding: '32px',
            overflow: 'auto',
            flex: 1,
            background: 'linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)',
          }}>
            {detailsModal.loading ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
              }}>
                <div className="spinner-enhanced"></div>
                <p style={{
                  marginTop: '20px',
                  color: '#0c2c6c',
                  fontSize: '16px',
                }}>
                  Cargando detalles...
                </p>
              </div>
            ) : detailsModal.error ? (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#d32f2f',
              }}>
                <p style={{ fontSize: '18px', fontWeight: '600' }}>Error</p>
                <p>{detailsModal.error}</p>
              </div>
            ) : detailsModal.records.length === 0 ? (
              <div style={{
                padding: '60px 40px',
                textAlign: 'center',
                color: '#666',
              }}>
                <div style={{
                  fontSize: '48px',
                  marginBottom: '16px',
                  opacity: 0.3,
                }}>📭</div>
                <p style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>No se encontraron registros</p>
                <p style={{ fontSize: '14px', color: '#999' }}>No hay datos que coincidan con los filtros aplicados</p>
              </div>
            ) : (
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}>
                {(() => {
                  // Obtener todas las columnas únicas de todos los registros (excluyendo "resumen")
                  const excludedColumns = new Set(['resumen', 'summary']);
                  const allColumns = new Set();
                  detailsModal.records.forEach(record => {
                    Object.keys(record).forEach(key => {
                      if (!excludedColumns.has(key.toLowerCase())) {
                        allColumns.add(key);
                      }
                    });
                  });
                  const columns = Array.from(allColumns).sort();
                  
                  // Función para formatear nombres de columnas - mejorada para evitar solapamiento
                  const formatColumnName = (col) => {
                    // Primero reemplazar guiones bajos con espacios
                    let formatted = col.replace(/_/g, ' ');
                    // Capitalizar primera letra de cada palabra
                    formatted = formatted.replace(/\b\w/g, l => l.toUpperCase());
                    // Dividir palabras largas compuestas para mejor legibilidad
                    // Ejemplo: "DURACIONLLAMADA" -> "DURACION LLAMADA"
                    formatted = formatted.replace(/([a-z])([A-Z])/g, '$1 $2');
                    return formatted;
                  };
                  
                  // Calcular ancho mínimo de columnas según el tipo de contenido
                  const getColumnWidth = (col) => {
                    const colLower = col.toLowerCase();
                    if (colLower.includes('cedula') || colLower.includes('id') || colLower.includes('documento')) {
                      return '140px';
                    }
                    if (colLower.includes('duration') || colLower.includes('duracion')) {
                      return '120px';
                    }
                    if (colLower.includes('time') || colLower.includes('fecha') || colLower.includes('date') || colLower.includes('inicio')) {
                      return '180px';
                    }
                    if (colLower.includes('status') || colLower.includes('estado') || colLower.includes('llamada')) {
                      return '130px';
                    }
                    if (colLower.includes('nombre') || colLower.includes('name')) {
                      return '200px';
                    }
                    if (colLower.includes('acepta') || colLower.includes('contactabilidad')) {
                      return '120px';
                    }
                    if (colLower.includes('cuotas') || colLower.includes('cuota')) {
                      return '100px';
                    }
                    return '150px'; // Ancho por defecto para otras columnas
                  };
                  
                  return (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      background: '#ffffff',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(12, 44, 108, 0.08)',
                    }}>
                      {/* Contenedor con scroll mejorado - Asegurar scroll horizontal y vertical */}
                      <div style={{
                        flex: 1,
                        overflowX: 'auto', // Scroll horizontal
                        overflowY: 'auto', // Scroll vertical
                        position: 'relative',
                        WebkitOverflowScrolling: 'touch', // Mejor scroll en móviles
                        minHeight: 0, // Importante para que el flex funcione correctamente
                      }}>
                        <table style={{
                          width: '100%',
                          minWidth: 'max-content', // Asegurar que la tabla no se corte - MUY IMPORTANTE
                          borderCollapse: 'separate',
                          borderSpacing: 0,
                          fontSize: '13px',
                          tableLayout: 'auto', // Distribución automática de columnas
                        }}>
                          <thead>
                            <tr style={{
                              background: 'linear-gradient(135deg, #0c2c6c 0%, #1a4a9c 100%)',
                              position: 'sticky',
                              top: 0,
                              zIndex: 10,
                            }}>
                              {columns.map((col, idx) => (
                                <th 
                                  key={col}
                                  style={{
                                    padding: '14px 12px',
                                    textAlign: 'left',
                                    fontWeight: '700',
                                    color: '#ffffff',
                                    whiteSpace: 'normal', // Permitir wrap para evitar solapamiento
                                    fontSize: '10px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px',
                                    borderRight: idx < columns.length - 1 ? '1px solid rgba(255, 255, 255, 0.15)' : 'none',
                                    minWidth: getColumnWidth(col),
                                    maxWidth: getColumnWidth(col),
                                    lineHeight: '1.3',
                                    wordBreak: 'break-word', // Romper palabras largas
                                  }}
                                  title={formatColumnName(col)} // Tooltip con nombre completo
                                >
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                  }}>
                                    <span>{formatColumnName(col)}</span>
                                  </div>
                                </th>
                              ))}
                              {/* Columna de acciones - solo para campos que permiten audio */}
                              {allowsAudio(detailsModal.fieldKey) ? (
                                <th 
                                  style={{
                                    padding: '14px 8px',
                                    textAlign: 'center',
                                    fontWeight: '700',
                                    color: '#ffffff',
                                    whiteSpace: 'nowrap',
                                    fontSize: '10px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px',
                                    minWidth: '240px',
                                    maxWidth: '240px',
                                    position: 'sticky',
                                    right: 0,
                                    background: 'linear-gradient(135deg, #0c2c6c 0%, #1a4a9c 100%)',
                                    zIndex: 11,
                                    borderRight: 'none',
                                  }}
                                >
                                  ACCIONES
                                </th>
                              ) : null}
                            </tr>
                          </thead>
                          <tbody>
                            {detailsModal.records.map((record, idx) => (
                              <tr 
                                key={idx}
                                style={{
                                  borderBottom: idx < detailsModal.records.length - 1 ? '1px solid rgba(12, 44, 108, 0.08)' : 'none',
                                  transition: 'all 0.2s ease',
                                  background: idx % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'rgba(244, 111, 66, 0.08)';
                                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(12, 44, 108, 0.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = idx % 2 === 0 ? '#ffffff' : '#f8f9fa';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                              >
                                {columns.map((col, colIdx) => {
                                  const value = record[col];
                                  let displayValue = '-';
                                  
                                  if (value !== null && value !== undefined) {
                                    // Si es una fecha/hora, formatearla
                                    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
                                      try {
                                        const date = new Date(value);
                                        if (!isNaN(date.getTime())) {
                                          displayValue = date.toLocaleString('es-ES', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                          });
                                        } else {
                                          displayValue = value;
                                        }
                                      } catch {
                                        displayValue = value;
                                      }
                                    } else {
                                      displayValue = String(value);
                                    }
                                  }
                                  
                                  // Detectar tipos de columnas para aplicar estilos especiales
                                  const colLower = col.toLowerCase();
                                  const isStatus = colLower.includes('status') || colLower.includes('estado') || colLower.includes('llamada');
                                  const isDuration = colLower.includes('duration') || colLower.includes('duracion');
                                  const isNumeric = typeof value === 'number' || (value !== null && !isNaN(Number(value)) && value !== '' && value !== '-');
                                  const isDate = colLower.includes('time') || colLower.includes('fecha') || colLower.includes('date') || colLower.includes('inicio') || colLower.includes('co');
                                  const isBoolean = colLower.includes('acepta') || colLower.includes('contactabilidad') || (typeof value === 'string' && (value.toLowerCase() === 'si' || value.toLowerCase() === 'no'));
                                  const statusValue = String(value || '').toLowerCase();
                                  
                                  return (
                                    <td 
                                      key={col}
                                      style={{
                                        padding: '12px 16px',
                                        color: isDate ? '#666' : '#333',
                                        fontFamily: isNumeric && !isStatus && !isBoolean ? 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace' : 'inherit',
                                        fontWeight: isNumeric && !isStatus && !isBoolean ? '600' : '400',
                                        fontSize: '13px',
                                        textAlign: 'left',
                                        borderRight: colIdx < columns.length - 1 ? '1px solid rgba(12, 44, 108, 0.05)' : 'none',
                                        whiteSpace: 'normal', // Permitir wrap para texto largo
                                        wordBreak: 'break-word', // Romper palabras largas
                                        maxWidth: getColumnWidth(col),
                                        minWidth: getColumnWidth(col),
                                        width: getColumnWidth(col), // Asegurar ancho fijo para evitar cortes
                                      }}
                                      title={displayValue !== '-' ? displayValue : ''} // Tooltip con el valor completo
                                    >
                                      {isStatus && value ? (
                                        <span style={{
                                          display: 'inline-block',
                                          padding: '5px 12px',
                                          borderRadius: '16px',
                                          fontSize: '10px',
                                          fontWeight: '700',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.3px',
                                          background: statusValue === 'done' || statusValue === 'completada' || statusValue === 'completed'
                                            ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.1) 100%)' 
                                            : statusValue === 'failed' || statusValue === 'fallida'
                                            ? 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(244, 67, 54, 0.1) 100%)'
                                            : 'linear-gradient(135deg, rgba(158, 158, 158, 0.15) 0%, rgba(158, 158, 158, 0.1) 100%)',
                                          color: statusValue === 'done' || statusValue === 'completada' || statusValue === 'completed'
                                            ? '#2e7d32'
                                            : statusValue === 'failed' || statusValue === 'fallida'
                                            ? '#c62828'
                                            : '#616161',
                                          border: `1px solid ${
                                            statusValue === 'done' || statusValue === 'completada' || statusValue === 'completed'
                                              ? 'rgba(76, 175, 80, 0.3)'
                                              : statusValue === 'failed' || statusValue === 'fallida'
                                              ? 'rgba(244, 67, 54, 0.3)'
                                              : 'rgba(158, 158, 158, 0.3)'
                                          }`,
                                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                        }}>
                                          {displayValue}
                                        </span>
                                      ) : isDuration && value ? (
                                        <span style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '5px',
                                          padding: '3px 10px',
                                          background: 'rgba(12, 44, 108, 0.05)',
                                          borderRadius: '6px',
                                          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                                          fontWeight: '600',
                                          color: '#0c2c6c',
                                          fontSize: '12px',
                                        }}>
                                          <span style={{ fontSize: '11px' }}>⏱️</span>
                                          {displayValue}
                                        </span>
                                      ) : isDate && value ? (
                                        <span style={{
                                          color: '#666',
                                          fontSize: '12px',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '5px',
                                        }}>
                                          <span>📅</span>
                                          {displayValue}
                                        </span>
                                      ) : isBoolean && value ? (
                                        <span style={{
                                          display: 'inline-block',
                                          padding: '4px 10px',
                                          borderRadius: '12px',
                                          fontSize: '11px',
                                          fontWeight: '600',
                                          background: statusValue === 'si' || statusValue === 'yes' || statusValue === 'true'
                                            ? 'rgba(76, 175, 80, 0.1)'
                                            : 'rgba(158, 158, 158, 0.1)',
                                          color: statusValue === 'si' || statusValue === 'yes' || statusValue === 'true'
                                            ? '#2e7d32'
                                            : '#616161',
                                        }}>
                                          {displayValue.toUpperCase()}
                                        </span>
                                      ) : isNumeric && value !== '-' ? (
                                        <span style={{
                                          color: '#0c2c6c',
                                          fontWeight: '600',
                                        }}>
                                          {displayValue}
                                        </span>
                                      ) : (
                                        <span style={{
                                          color: value && value !== '-' ? '#333' : '#999',
                                          fontStyle: value && value !== '-' ? 'normal' : 'italic',
                                        }}>
                                          {displayValue}
                                        </span>
                                      )}
                                    </td>
                                  );
                                })}
                                {/* Celda de acciones - solo para campos que permiten audio */}
                                {allowsAudio(detailsModal.fieldKey) ? (
                                  <td 
                                    style={{
                                      padding: '10px 8px',
                                      textAlign: 'center',
                                      borderRight: 'none',
                                      position: 'sticky',
                                      right: 0,
                                      background: idx % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                      zIndex: 5,
                                      minWidth: '240px',
                                      maxWidth: '240px',
                                      verticalAlign: 'middle',
                                    }}
                                  >
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '6px',
                                      justifyContent: 'center',
                                      alignItems: 'stretch',
                                      width: '100%',
                                    }}>
                                      {/* Botón Ver/Descargar Transcripción */}
                                      <button
                                        onClick={() => handleViewTranscript(record)}
                                        style={{
                                          padding: '8px 12px',
                                          borderRadius: '8px',
                                          border: '1px solid rgba(12, 44, 108, 0.25)',
                                          background: 'linear-gradient(135deg, rgba(12, 44, 108, 0.12) 0%, rgba(12, 44, 108, 0.08) 100%)',
                                          color: '#0c2c6c',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '6px',
                                          transition: 'all 0.2s ease',
                                          whiteSpace: 'nowrap',
                                          width: '100%',
                                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.background = 'rgba(12, 44, 108, 0.18)';
                                          e.currentTarget.style.transform = 'translateY(-1px)';
                                          e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(12, 44, 108, 0.12) 0%, rgba(12, 44, 108, 0.08) 100%)';
                                          e.currentTarget.style.transform = 'translateY(0)';
                                          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                        }}
                                        title="Ver/Descargar Transcripción"
                                      >
                                        <span style={{ fontSize: '14px' }}>📄</span>
                                        <span>Transcripción</span>
                                      </button>
                                      
                                      {/* Botón Descargar Audio */}
                                      <button
                                        onClick={() => handleDownloadAudio(record)}
                                        style={{
                                          padding: '8px 12px',
                                          borderRadius: '8px',
                                          border: '1px solid rgba(244, 111, 66, 0.35)',
                                          background: 'linear-gradient(135deg, rgba(244, 111, 66, 0.12) 0%, rgba(244, 111, 66, 0.08) 100%)',
                                          color: '#f46f42',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '6px',
                                          transition: 'all 0.2s ease',
                                          whiteSpace: 'nowrap',
                                          width: '100%',
                                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.background = 'rgba(244, 111, 66, 0.18)';
                                          e.currentTarget.style.transform = 'translateY(-1px)';
                                          e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(244, 111, 66, 0.12) 0%, rgba(244, 111, 66, 0.08) 100%)';
                                          e.currentTarget.style.transform = 'translateY(0)';
                                          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                        }}
                                        title="Descargar Audio"
                                      >
                                        <span style={{ fontSize: '14px' }}>⬇️</span>
                                        <span>Descargar Audio</span>
                                      </button>
                                      
                                      {/* Botón Reproducir Audio */}
                                      <button
                                        onClick={() => handlePlayAudio(record)}
                                        style={{
                                          padding: '8px 12px',
                                          borderRadius: '8px',
                                          border: '1px solid rgba(76, 175, 80, 0.35)',
                                          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.12) 0%, rgba(76, 175, 80, 0.08) 100%)',
                                          color: '#4caf50',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '6px',
                                          transition: 'all 0.2s ease',
                                          whiteSpace: 'nowrap',
                                          width: '100%',
                                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.background = 'rgba(76, 175, 80, 0.18)';
                                          e.currentTarget.style.transform = 'translateY(-1px)';
                                          e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(76, 175, 80, 0.12) 0%, rgba(76, 175, 80, 0.08) 100%)';
                                          e.currentTarget.style.transform = 'translateY(0)';
                                          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                        }}
                                        title="Reproducir Audio"
                                      >
                                        <span style={{ fontSize: '14px' }}>▶️</span>
                                        <span>Escuchar</span>
                                      </button>
                                    </div>
                                  </td>
                                ) : null}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Modal del Reproductor de Audio */}
    {audioPlayer.isOpen && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(12, 44, 108, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '20px',
        }}
        onClick={closeAudioPlayer}
      >
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            maxWidth: '600px',
            width: '100%',
            padding: '32px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: '700',
              color: '#0c2c6c',
            }}>
              🎵 Reproducir Llamada
            </h3>
            <button
              onClick={closeAudioPlayer}
              style={{
                background: 'rgba(12, 44, 108, 0.1)',
                border: 'none',
                borderRadius: '8px',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0c2c6c',
                fontSize: '24px',
                fontWeight: 'bold',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(12, 44, 108, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(12, 44, 108, 0.1)';
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            borderRadius: '12px',
            padding: '24px',
            border: '2px solid rgba(12, 44, 108, 0.1)',
          }}>
            <audio
              controls
              autoPlay
              style={{
                width: '100%',
                height: '50px',
              }}
            >
              <source src={audioPlayer.audioUrl} type="audio/mpeg" />
              <source src={audioPlayer.audioUrl} type="audio/mp3" />
              Tu navegador no soporta el elemento de audio.
            </audio>
          </div>
          
          <div style={{
            fontSize: '14px',
            color: '#666',
            textAlign: 'center',
          }}>
            <p style={{ margin: 0 }}>
              ID Conversación: <strong>{audioPlayer.conversationId}</strong>
            </p>
          </div>
        </div>
      </div>
    )}
  </div>
  );
};

export default AgentAnalyticsPanel;